import { Box, Container, Typography, Card, CardContent, Grid } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useI18n } from '../../utils/i18n';
import useScrollReveal from '../../hooks/useScrollReveal';

const FEATURES = [
  { icon: TrendingUpIcon, titleKey: 'landing.features.stockAnalysis.title', descKey: 'landing.features.stockAnalysis.desc' },
  { icon: AutoAwesomeIcon, titleKey: 'landing.features.aiRecommendations.title', descKey: 'landing.features.aiRecommendations.desc' },
  { icon: PsychologyIcon, titleKey: 'landing.features.marketEmotion.title', descKey: 'landing.features.marketEmotion.desc' },
];

function FeatureCard({ icon: Icon, titleKey, descKey, index }) {
  const { t } = useI18n();
  const ref = useScrollReveal({ delay: index * 100 });

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Card
        ref={ref}
        sx={{
          height: '100%',
          bgcolor: 'background.paper',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 3,
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(108,99,255,0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #6C63FF, #00D9A6)',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
            {t(titleKey)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {t(descKey)}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

/**
 * Feature showcase section — 3 cards in a responsive grid with scroll-reveal animation.
 * Accepts `id` prop for anchor scrolling from the navbar.
 */
export default function FeatureShowcase({ id }) {
  const { t } = useI18n();
  const titleRef = useScrollReveal();

  return (
    <Box id={id} sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Typography
          ref={titleRef}
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}
        >
          {t('landing.features.title')}
        </Typography>

        <Grid container spacing={3}>
          {FEATURES.map((feature, index) => (
            <FeatureCard key={feature.titleKey} {...feature} index={index} />
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
